<!--
    Displays a view inside the renderer component.
-->
<template>
    <div ref="container" class="absolute inset-0">
        <div
            v-if="!dependent"
            ref="image"
            class="cz2__image h-full w-full"
        ></div>

        <template v-if="!embedded">
            <layout-editor
                v-for="(placement, placementIndex) in layoutPlacements"
                :key="placement.placement.code"
                :class="['cz2__client-image-view-editor']"
                :showBorders="showBorders"
                :portalName="portalName"
                :index="placementIndex"
                :controller="controller"
                :view="selectedView"
                :frame="selectedFrame"
                :placement="placement"
                :enabled="editorEnabled(placement)"
                :squareHandles="true"
                :layerScale="layerScale"
                :secondary="isSecondary"></layout-editor>

            <div class="cz2__client-image-editor-overlays">
                <portal-target :name="portalName" multiple>
                </portal-target>
            </div>
        </template>
    </div>
</template>

<style lang="scss">
    @import "../style/variables.scss";

    .cz2__image {
        .cz__render {
            // flex flex-col flex-1 h-full items-center justify-center
            display: flex;
            flex-direction: column;
            flex: 1;
            justify-content: center;
            align-items: center;
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
        }
    }

    #cz2.cz2 {
        .cz2__client-image-editor-overlays {
            position: absolute;

            left: 0;
            top: 0;

            width: 100%;
            height: 100%;

            pointer-events: none;
        }
    }
</style>

<script>
    import ResizeObserver from 'resize-observer-polyfill';

    import LayoutEditor from './LayoutEditor.vue';

    import unique from '../utilities/unique';
    import { RendererEventBus, RendererEvents } from '../utilities/UI/events/renderer';

    import designArea from '../utilities/designArea';

    const { RESIZE } = RendererEvents;

    export default {
        props: {
            embedded: Boolean,
            dependent: Boolean,

            view: {
                type: String,
                default: '',
            },

            controller: {
                type: Object,
                default: null,
            },
        },
        components: {
            LayoutEditor,
        },
        data() {
            return {
                portalName: `editor-overlays-${unique()}`,

                renderedSizing: null,

                selectedFrame: null,
            };
        },
        computed: {
            globalOptions() {
                return this.$store.state.options;
            },

            /**
             * Current selected step.
             */
            selectedStep() {
                return this.$store.state.selectedStep;
            },

            windowWidth() {
                return this.$store.state.windowWidth;
            },

            layoutPlacements() {
                if (this.controller && this.controller.blueprint && this.controller.state) {
                    const placements = this.controller.state.filter((s) => s.placement.custom['layout-editor'] === 'true');

                    placements.sort((a, b) => {
                        const aIndex = +a.placement.custom['explicit-order'];
                        const bIndex = +b.placement.custom['explicit-order'];

                        return aIndex - bIndex;
                    });

                    return placements;
                }

                return [];
            },

            selectedView() {
                if (this.controller && this.controller.blueprint && this.controller.state) {
                    return this.controller.blueprint.views.find((v) => v.code === this.view);
                }

                return null;
            },

            editPlacements() {
                return this.$store.state.customizer.editPlacements;
            },

            layerScale() {
                // x1 scale is probably ok for Logomark since every layout is separate area - we don't have wrap around layouts.
                return 1;
            },

            /**
             * Currently selected bottle.
             */
            selectedBottle() {
                return this.$store.state.customizer.selectedBottle;
            },

            /*
             * All bottles list.
             */
            secondaryBottles() {
                return this.$store.state.customizer.secondaryBottles || [];
            },

            isSecondary() {
                return this.secondaryBottles.indexOf(this.selectedBottle) > 0;
            },

            selectedStepV3() {
                return this.$store.state.selectedStepV3;
            },

            appVersion() {
                return this.$store.state.appVersion;
            },

            currentView() {
                return this.$store.state.customizer.currentView;
            },

            showBorders() {
                let result = false;

                if (this.appVersion === '3') {
                    result = !(
                        this.selectedStepV3 === 'review'
                        || this.selectedStepV3 === 'start'
                        || this.selectedStepV3 === 'itemColors'
                    );
                } else {
                    result = !(
                        this.selectedStep === 'review'
                    );
                }

                return result;
            },
        },
        watch: {
            controller() {
                if (this.image) {
                    this.image.adoptController(this.controller);
                    this.image.selectViewByCode(this.view);

                    this.selectedFrame = this.image.selectedFrame();
                } else {
                    this.mountController();
                }
            },
        },
        mounted() {
            // Resize the view when window size changes.
            this.resize();
            window.addEventListener('resize', this.resizeWindow);

            this.mountController();
            RendererEventBus.$on(RESIZE, this.resize);

            if (this.$refs.image) {
                this.resizeObserver = new ResizeObserver(() => {
                    this.resize();
                });
                this.resizeObserver.observe(this.$refs.image);
            }
        },
        beforeDestroy() {
            if (this.image) {
                this.image.destroy();

                this.image = null;
            }

            window.removeEventListener('resize', this.resizeWindow);

            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
        },
        methods: {
            /**
             * Resize after window resize.
             */
            resizeWindow() {
                setTimeout(() => {
                    // Wait a bit after resizing to allow parent container to respond first.
                    // Waiting using a timer since we are not sure when Swiper will resize slides.
                    this.resize();
                }, 10);
            },

            /**
             * Resizes image slide.
             */
            resize() {
                if (!this.$refs.container) {
                    return;
                }

                if (this.image) {
                    this.image.resize();
                }
            },

            mountController() {
                if (this.image) {
                    this.image.off();
                    this.image.destroy();
                }

                if (this.controller && !this.dependent) {
                    this.image = new this.globalOptions.DriveCustomizer.ImageView(this.controller, {
                        container: this.$refs.image,
                        preloadViews: false,
                    });

                    this.image.on('rendered', this.rendered);

                    this.image.on('error', this.errored);

                    this.image.on('notfound', this.errored);

                    this.image.selectViewByCode(this.view);

                    this.selectedFrame = this.image.selectedFrame();
                } else {
                    this.selectedFrame = this.selectedView.frameLow;
                }
            },

            errored() {
                // eslint-disable-next-line
                console.log(`Failed to render: ${this.view}`);

                this.$emit('hide');
            },

            rendered(sizing) {
                if (sizing) {
                    this.renderedSizing = sizing;

                    this.$emit('rendered', sizing);
                }
            },

            editorEnabled(placement) {
                if (this.currentView && this.selectedView && this.currentView.view.code !== this.selectedView.code) {
                    return false;
                }

                if (this.editPlacements && this.editPlacements.some((p) => p.code === placement.placement.code)) {
                    const { component } = placement;

                    if (component.custom['widget-type'] === 'personalization') {
                        return (component.custom['personalization-text'] || '').length > 0;
                    }

                    if (component.custom['widget-type'] === 'dynamic-image') {
                        return (component.custom['dynamic-image-url'] || '').length > 0;
                    }
                }

                return false;
            },
        },
    };
</script>
